import { FilterDefinition } from '../models/FilterDefinition/FilterDefinition';
import {
  FilterSelections,
  GraphQLQuery,
  RelationalOperator,
} from '../types/filter';
import { Mapper } from './Mapper';

export interface FilterDefinitionGraphQLQueryMapperInput {
  filterDefinitions: FilterDefinition[];
  filterSelections: FilterSelections;
}

export class FilterDefinitionGraphQLQueryMapper implements Mapper<
  FilterDefinitionGraphQLQueryMapperInput,
  GraphQLQuery<unknown>[]
> {
  map({
    filterDefinitions,
    filterSelections,
  }: FilterDefinitionGraphQLQueryMapperInput): GraphQLQuery<unknown>[] {
    const conditions: GraphQLQuery<unknown>[] = [];
    const orGroups: GraphQLQuery<unknown>[] = [];

    for (const filterDef of filterDefinitions) {
      const condition = this.buildConditionForFilter(
        filterDef,
        filterSelections
      );
      if (condition) {
        conditions.push(condition);
      }
    }

    if (orGroups.length > 0) {
      conditions.push({ and: orGroups });
    }

    return conditions;
  }

  private buildConditionForFilter(
    filterDef: FilterDefinition,
    filterSelections: FilterSelections
  ): GraphQLQuery<unknown> | null {
    const propertyName = filterDef.databaseMetadata?.columnName;
    const fieldId = filterDef.id;
    const filterSelection = filterSelections[fieldId];

    // Exclude fields without selection or without database metadata
    if (
      !filterSelection ||
      !propertyName ||
      !filterDef.databaseMetadata ||
      !filterDef.isDatabaseField
    ) {
      return null;
    }

    const values = filterSelection.value;
    if (values.length === 0) {
      return null;
    }
    const dataType = filterDef.databaseMetadata.dataType;

    // Handle date ranges
    if (dataType === 'date') {
      return this.buildDateCondition(propertyName, values);
    }

    // Handle array fields with semantic operators
    if (filterDef.isArrayField) {
      return this.buildArrayCondition(
        propertyName,
        filterSelection.relationalOperator,
        values,
        dataType
      );
    }

    // Handle regular conditions
    const relationalOperator = convertRelationalOperatorToGraphQL(
      filterSelection.relationalOperator
    );
    return this.buildRegularCondition(
      propertyName,
      relationalOperator,
      values,
      dataType
    );
  }

  private buildArrayCondition(
    propertyName: string,
    operator: RelationalOperator,
    values: unknown[],
    dataType: string
  ): GraphQLQuery<unknown> {
    const formatValue = this.createValueFormatter(dataType);
    const nonNullValues = values.filter(
      val => !this.isNullValue(val, dataType)
    );

    if (nonNullValues.length === 0) {
      return {};
    }

    const eqConditions = nonNullValues.map(val => ({
      [propertyName]: { eq: formatValue(val) },
    }));

    switch (operator) {
      case RelationalOperator.CONTAINS_ANY:
      case RelationalOperator.EQUAL:
        // Record contains ANY of the selected values: OR logic
        return eqConditions.length === 1
          ? eqConditions[0]
          : { or: eqConditions };

      case RelationalOperator.CONTAINS_NONE:
      case RelationalOperator.NOT_EQUAL:
        // Record contains NONE of the selected values: NOT(OR(...))
        return {
          not:
            eqConditions.length === 1 ? eqConditions[0] : { or: eqConditions },
        };

      case RelationalOperator.CONTAINS_ALL:
        // Record contains ALL of the selected values: AND logic
        return eqConditions.length === 1
          ? eqConditions[0]
          : { and: eqConditions };

      default:
        throw new Error(`Unsupported array operator: ${operator}`);
    }
  }

  private buildDateCondition(
    propertyName: string,
    values: unknown[]
  ): GraphQLQuery<Record<string, unknown>> {
    const [startDate, endDate] = values.map(v =>
      v ? new Date(v.toString()) : undefined
    );
    const dateCondition: GraphQLQuery<Record<string, unknown>> = {};

    if (startDate && !endDate) {
      dateCondition[propertyName] = {
        gte: startDate.toISOString(),
      };
    } else if (!startDate && endDate) {
      const next = new Date(endDate.getTime() + 86400000);
      dateCondition[propertyName] = { lt: next.toISOString() };
    } else if (startDate && endDate) {
      const next = new Date(endDate.getTime() + 86400000);
      dateCondition[propertyName] = {
        gte: startDate.toISOString(),
        lt: next.toISOString(),
      };
    }

    return dateCondition;
  }

  private buildRegularCondition(
    propertyName: string,
    relationalOperator: string,
    values: unknown[],
    dataType: string
  ): GraphQLQuery<unknown> {
    const formatValue = this.createValueFormatter(dataType);
    const nonNullValues = values.filter(
      val => !this.isNullValue(val, dataType)
    );
    const nullValues = values.filter(val => this.isNullValue(val, dataType));

    const orConditions: GraphQLQuery<unknown>[] = [];

    // Handle non-null values
    if (nonNullValues.length > 0) {
      const nonNullOrConditions = nonNullValues.map(val => ({
        [propertyName]: { [relationalOperator]: formatValue(val) },
      }));
      orConditions.push(...nonNullOrConditions);
    }

    // Handle null values
    if (nullValues.length > 0) {
      const nullCondition = this.buildNullCondition(
        propertyName,
        relationalOperator,
        dataType
      );
      if (nullCondition) {
        orConditions.push(nullCondition);
      }
    }

    // Return single condition or OR combination
    if (orConditions.length === 1) {
      return orConditions[0];
    } else if (orConditions.length > 1) {
      return { or: orConditions };
    }

    return {};
  }

  private buildNullCondition(
    propertyName: string,
    relationalOperator: string,
    dataType: string
  ): GraphQLQuery<unknown> | null {
    if (relationalOperator === 'eq') {
      if (dataType === 'number') {
        return {
          not: {
            or: [
              { [propertyName]: { gte: 0 } },
              { [propertyName]: { lte: 0 } },
            ],
          },
        };
      } else {
        return {
          [propertyName]: {
            exists: false,
          },
        };
      }
    } else if (relationalOperator === 'ne') {
      if (dataType === 'number') {
        return {
          or: [{ [propertyName]: { gte: 0 } }, { [propertyName]: { lte: 0 } }],
        };
      } else {
        return {
          [propertyName]: {
            exists: true,
          },
        };
      }
    }
    return null;
  }

  private createValueFormatter(dataType: string) {
    return (val: unknown) => {
      if (val === null || val === undefined) return null;
      switch (dataType) {
        case 'string':
          return String(val);
        case 'number':
          return Number(val);
        case 'boolean':
          return val === 'true' || val === true;
        case 'date':
          return new Date(val.toString()).toISOString();
        default:
          return val;
      }
    };
  }

  private isNullValue(val: unknown, dataType: string): boolean {
    if (val === null || val === undefined) return true;
    return dataType === 'string' && val === '';
  }
}

/**
 * Converts a RelationalOperator to a string representation for GraphQL.
 *
 * @param relationalOperator - The operator to convert.
 * @returns The string representation of the operator.
 */
export const convertRelationalOperatorToGraphQL = (
  relationalOperator: RelationalOperator
): string => {
  switch (relationalOperator) {
    case RelationalOperator.NOT_EQUAL:
      return 'ne';
    case RelationalOperator.EQUAL:
      return 'eq';
    case RelationalOperator.GREATER_THAN_OR_EQUAL:
      return 'gte';
    case RelationalOperator.LESS_THAN_OR_EQUAL:
      return 'lte';
    case RelationalOperator.GREATER_THAN:
      return 'gt';
    case RelationalOperator.LESS_THAN:
      return 'lt';
    // Array operators are handled differently in buildArrayCondition
    case RelationalOperator.CONTAINS_ANY:
    case RelationalOperator.CONTAINS_NONE:
    case RelationalOperator.CONTAINS_ALL:
      return 'eq'; // Default to eq, actual logic handled in buildArrayCondition
    default:
      throw new Error(`Unsupported relational operator: ${relationalOperator}`);
  }
};
